import { useState } from 'react';
import type { TableProps } from 'antd';
import { Switch } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, SafetyOutlined } from '@ant-design/icons';
import { Inbox } from 'lucide-react';

import { DataTable, MenuThaoTac, Tile } from '../../../components/shared';
import { Badge, Button, Empty, Select } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { ngayVN, soVN } from '../../../lib/dinh-dang';
import { KhoiLoi } from '../../nhat-ky-truy-vet/chung/components/KhoiLoi';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import { beRongThanh, hienPhanTram, phanTramDaDung } from '../../tenant/nghiep-vu/han-muc-token';
import { CHU_KY, HANH_DONG, PHAM_VI, TONG_MUC_HAN_MUC } from '../constants';
import { useBatHanMucAi, useHanMucAi, usePhanRa, useXoaHanMucAi } from '../hooks/useTokenAi';
import { cacThang, mucHanMucAi } from '../nghiep-vu/token-ai';
import type { AiLimit, PhamViHanMucAi } from '../types';
import { HopHanMucAi } from './HopHanMucAi';

type LocPhamVi = PhamViHanMucAi | 'tat-ca';
type HopDangMo = { kieu: 'tao' } | { kieu: 'sua'; dong: AiLimit };

function TenTinhNang({ ten }: { ten: string }) {
  return (
    <span className="block max-w-[8rem] truncate text-sm text-content-secondary" title={ten}>
      {ten}
    </span>
  );
}

function OTienDo({ h }: { h: AiLimit }) {
  const { t, locale } = useT();
  const muc = mucHanMucAi(h);
  const pct = phanTramDaDung(h.da_dung, h.gioi_han);
  const hien = hienPhanTram(pct);
  const tong = TONG_MUC_HAN_MUC[muc];
  return (
    <div className={cn('w-44 space-y-1', !h.bat && 'opacity-60')}>
      <div className="flex items-baseline justify-between gap-2 text-xs tabular-nums">
        <span className="text-content-primary">
          {t('manTokenAi.daDungTren', { daDung: soVN(h.da_dung, locale), gioiHan: soVN(h.gioi_han, locale) })}
        </span>
        <span className={cn('font-semibold', tong.chu)}>{hien.vuot ? '>100%' : `${soVN(hien.so, locale)}%`}</span>
      </div>
      <div
        role="progressbar"
        aria-label={t('manTokenAi.cotDaDung')}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={beRongThanh(pct)}
        aria-valuetext={t('manTokenAi.daDungTren', { daDung: soVN(h.da_dung, locale), gioiHan: soVN(h.gioi_han, locale) })}
        className="h-1.5 w-full overflow-hidden rounded-full bg-subtle"
      >
        <div className={cn('h-full rounded-full', tong.thanh)} style={{ width: `${beRongThanh(pct)}%` }} />
      </div>
      <p className="text-2xs text-content-muted">{t('manTokenAi.moLai', { ngay: ngayVN(h.resets_at) })}</p>
    </div>
  );
}

/**
 * Thẻ Hạn mức (ADR-0123 mục 6): Tenant Admin đặt trần con trong gói cho phòng ban hoặc người dùng. Lọc phạm vi ở server.
 * Bật tắt ngay trên dòng; sửa, xoá qua `MenuThaoTac`.
 */
export function TabHanMuc({ tenantId }: { tenantId: string }) {
  const { t } = useT();
  const dichLoi = useLoi();
  const [loc, setLoc] = useState<LocPhamVi>('tat-ca');
  const [hop, setHop] = useState<HopDangMo | null>(null);
  const [canXoa, setCanXoa] = useState<AiLimit | null>(null);
  const q = useHanMucAi(tenantId, loc === 'tat-ca' ? undefined : loc);
  const bat = useBatHanMucAi(tenantId);
  const xoa = useXoaHanMucAi(tenantId);
  // Tên tính năng lấy từ số liệu tháng này; chưa có thì hiện mã.
  const [thangNay] = useState(() => cacThang(Date.now(), 0)[0] ?? '');
  const qTinhNang = usePhanRa(tenantId, thangNay, 'TINH_NANG');
  const tenTinhNang = new Map((qTinhNang.data ?? []).filter((d) => d.khoa).map((d) => [d.khoa, d.ten]));

  const ds = q.data ?? [];
  const coLoc = loc !== 'tat-ca';
  const tenDoiTuong = (h: AiLimit) => h.ten_khoa ?? t('manTokenAi.doiTuongKhongCon');

  const columns: TableProps<AiLimit>['columns'] = [
    {
      title: t('manTokenAi.cotPhamVi'),
      width: 100,
      render: (_v, h) => <Badge variant={PHAM_VI[h.pham_vi].tong}>{t(PHAM_VI[h.pham_vi].nhan)}</Badge>,
    },
    {
      title: t('manTokenAi.cotDoiTuong'),
      width: 180,
      render: (_v, h) => (
        <span
          className={cn('block max-w-[11rem] truncate text-sm', h.ten_khoa ? 'font-medium text-content-primary' : 'text-content-muted')}
          title={h.ten_khoa ?? h.khoa}
        >
          {tenDoiTuong(h)}
        </span>
      ),
    },
    {
      title: t('manTokenAi.cotTinhNang'),
      width: 140,
      render: (_v, h) => (
        <TenTinhNang ten={h.tinh_nang ? (tenTinhNang.get(h.tinh_nang) ?? h.tinh_nang) : t('manTokenAi.moiTinhNang')} />
      ),
    },
    {
      title: t('manTokenAi.cotChuKy'),
      width: 70,
      render: (_v, h) => <span className="whitespace-nowrap text-sm text-content-secondary">{t(CHU_KY[h.chu_ky])}</span>,
    },
    { title: t('manTokenAi.cotDaDung'), width: 190, render: (_v, h) => <OTienDo h={h} /> },
    {
      title: t('manTokenAi.cotHanhDong'),
      width: 110,
      render: (_v, h) => <Badge variant={HANH_DONG[h.hanh_dong].tong}>{t(HANH_DONG[h.hanh_dong].nhan)}</Badge>,
    },
    {
      title: t('manTokenAi.cotBat'),
      width: 56,
      align: 'center',
      render: (_v, h) => (
        <Switch
          size="small"
          checked={h.bat}
          disabled={bat.isPending}
          aria-label={t('manTokenAi.batNhan', { ten: tenDoiTuong(h) })}
          onChange={(v) => bat.mutate({ id: h.id, bat: v })}
        />
      ),
    },
    {
      title: t('chung.thaoTac'),
      width: 72,
      align: 'center',
      fixed: 'right',
      render: (_v, h) => (
        <MenuThaoTac
          tenBanGhi={tenDoiTuong(h)}
          muc={[
            { khoa: 'sua', nhan: t('manTokenAi.suaHanMuc'), icon: <EditOutlined />, nhom: 'sua', onChon: () => setHop({ kieu: 'sua', dong: h }) },
            {
              khoa: 'xoa',
              nhan: t('manTokenAi.xoaHanMuc'),
              icon: <DeleteOutlined />,
              nhom: 'pha-huy',
              onChon: () => {
                xoa.reset();
                setCanXoa(h);
              },
            },
          ]}
        />
      ),
    },
  ];

  const nutTao = (
    <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setHop({ kieu: 'tao' })}>
      {t('manTokenAi.themHanMuc')}
    </Button>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
        <Select<LocPhamVi>
          className="w-full sm:w-48"
          aria-label={t('manTokenAi.locPhamVi')}
          value={loc}
          onChange={setLoc}
          options={[
            { value: 'tat-ca', label: t('manTokenAi.moiPhamVi') },
            { value: 'DON_VI', label: t('manTokenAi.phamViDonVi') },
            { value: 'NGUOI_DUNG', label: t('manTokenAi.phamViNguoi') },
          ]}
        />
        {nutTao}
        <p className="w-full text-xs leading-relaxed text-content-muted">{t('manTokenAi.giaiThich')}</p>
      </div>

      <Tile className="overflow-hidden">
        {q.error && !q.data ? (
          <KhoiLoi loi={q.error} onThuLai={() => void q.refetch()} />
        ) : (
          <DataTable<AiLimit>
            caption={t('manTokenAi.bangCaption')}
            rowKey="id"
            dataSource={ds}
            columns={columns}
            loading={q.isLoading}
            size="small"
            pagination={false}
            locale={{
              emptyText: q.isLoading ? (
                ' '
              ) : coLoc ? (
                <Empty icon={Inbox} title={t('manTokenAi.rongLoc')} />
              ) : (
                <Empty
                  icon={SafetyOutlined}
                  title={t('manTokenAi.rongHanMuc')}
                  description={t('manTokenAi.rongHanMucMo')}
                  action={nutTao}
                />
              ),
            }}
          />
        )}
      </Tile>

      {hop && (
        <HopHanMucAi
          tenantId={tenantId}
          dang={hop.kieu === 'sua' ? hop.dong : null}
          tinhNangCoSan={qTinhNang.data ?? []}
          onClose={() => setHop(null)}
        />
      )}
      <HopXacNhanThaoTac
        open={Boolean(canXoa)}
        tieuDe={t('manTokenAi.xoaTieuDe', { ten: canXoa ? tenDoiTuong(canXoa) : '' })}
        than={t('manTokenAi.xoaThan')}
        nhanXacNhan={t('manTokenAi.xoaHanMuc')}
        nguyHiem
        dangChay={xoa.isPending}
        loi={xoa.error ? dichLoi(xoa.error) : null}
        onClose={() => setCanXoa(null)}
        onXacNhan={() => canXoa && xoa.mutate(canXoa.id, { onSuccess: () => setCanXoa(null) })}
      />
    </div>
  );
}
